import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { subscribeConsent, setMarketingConsent } from '../utils/consentStore';
import { isGoogleAdsEnabled, grantConsentAndLoad, denyConsent } from '../utils/googleAds';
import { isPublicMarketingPath, buildPublicPath } from '../constants/publicRoutes';
import { moveFocusAfterDismiss } from '../utils/focusAfterDismiss';
import { useBannerHeightCssVar } from '../hooks/useBannerHeightCssVar';
import PoliteAnnouncer from './PoliteAnnouncer';
import './CookieConsentBanner.css';

/** Wird auf <html> gesetzt, solange der Banner sichtbar ist. */
const BANNER_VISIBLE_CLASS = 'cookie-consent-visible';

/**
 * Cookie-/Consent-Banner für Google Ads Werbemessung (Consent Mode v2).
 *
 * Sichtbarkeitsbedingungen (ALLE müssen zutreffen):
 *  1. Nach dem Mount (Hydration-Sicherheit, siehe useEffect unten).
 *  2. Aktuelle Route ist eine öffentliche Marketing-Seite
 *     (isPublicMarketingPath).
 *  3. Noch keine Entscheidung getroffen (getMarketingConsent() === null).
 *  4. Google Ads ist überhaupt konfiguriert (isGoogleAdsEnabled()) — ohne
 *     konfigurierte Tag-ID wird bewusst keine Einwilligung für etwas
 *     eingeholt, das ohnehin nicht stattfindet.
 *
 * Bewusst KEIN role="dialog" und KEIN Fokus-Trap: Der Banner blockiert
 * nichts — ohne Einwilligung lädt schlicht kein Skript. Ein Trap auf einem
 * nicht-blockierenden Banner würde Tastaturnutzer bei jedem Seitenaufruf
 * unnötig festsetzen.
 *
 * "Ablehnen" und "Akzeptieren" sind bewusst gleichwertige Buttons (gleiche
 * Größe/Prominenz UND gleiche Füllung/Kontrast — siehe MAJOR-6-Fix bei den
 * Button-Definitionen unten, "Ablehnen" steht im DOM VOR "Akzeptieren",
 * kein Textlink) — Vorgabe der Datenschutzkonferenz gegen Dark Patterns bei
 * Cookie-Bannern.
 *
 * scripts/prerender.js setzt die Ads-ID fuer Snapshots ausdruecklich leer.
 * Dadurch bleibt der Banner aus dem statischen HTML heraus und erscheint
 * erst nach der Hydration anhand der echten Runtime-Konfiguration.
 *
 * Der weiter unten dauerhaft gemountete PoliteAnnouncer (CRITICAL-1-Fix)
 * ist von dieser Neutralisierung NICHT betroffen — er landet unabhängig von
 * "show" im statischen HTML der Marketing-Seiten, allerdings nur als
 * leeres, visuell verstecktes <div id="cookie-consent-announcer"
 * role="status">. Das ist unschädlich (kein sichtbarer/vorlesbarer Inhalt,
 * kein Hydration-Mismatch, da der Client ihn ebenfalls leer initial
 * rendert) und beabsichtigt: genau dieses dauerhafte Mounten ist die
 * Voraussetzung dafür, dass die Live-Region beim ersten echten
 * Sichtbarwerden des Banners bereits im Accessibility-Tree registriert ist.
 */
const CookieConsentBanner = () => {
  const location = useLocation();

  // Erst nach dem Mount aktiv werden: vermeidet Hydration-Mismatch, weil
  // scripts/prerender.js u.a. '/rentinger', '/rentinger/datenschutz' etc.
  // statisch vorrendert (index.js nutzt hydrateRoot). Ohne dieses Muster
  // würde der Banner ins statische HTML gebacken.
  const [mounted, setMounted] = useState(false);
  // MAJOR-4-Fix: reaktiv über subscribeConsent() statt einmaligem Read.
  // /datenschutz steht selbst in PUBLIC_MARKETING_ROUTES — dort sind bei
  // unentschiedenem Consent Banner UND das Inline-Panel aus PrivacyPage.js
  // (Abschnitt 5.3) gleichzeitig sichtbar. Ohne Subscription würde ein Klick
  // auf "Einwilligung erteilen" im Inline-Panel den Banner nicht schließen,
  // weil dessen lokaler State nie erneut gelesen wird.
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    setMounted(true);
    // subscribeConsent() ruft den Callback SOFORT mit dem aktuellen Stand
    // auf (liefert also gleich den initialen Wert) und danach bei jeder
    // Änderung — egal ob ausgelöst über diesen Banner, das Inline-Panel auf
    // /datenschutz oder eine andere Komponente.
    const unsubscribe = subscribeConsent(setConsent);
    return unsubscribe;
  }, []);

  // MAJOR-3-Fix: location.pathname ist bewusst Teil der Dependencies (kein
  // eslint-disable mehr). Der Banner wird in App.js EINMALIG außerhalb von
  // <Routes> gemountet und bei SPA-Navigation nie neu gemountet — mit der
  // alten Abhängigkeit [mounted] lief dieser Effekt nur beim initialen
  // Seitenaufbau. Szenario: Ein Nutzer mit bereits erteilter Einwilligung
  // lädt direkt /dashboard (keine öffentliche Route) und navigiert danach
  // clientseitig zu /register oder /datenschutz — ohne diesen Fix würde
  // grantConsentAndLoad() in dieser Sitzung NIE aufgerufen, obwohl eine
  // gültige Einwilligung vorliegt. loadGoogleAds() ist zusätzlich
  // idempotent (scriptInjected-Guard in googleAds.js), wiederholtes
  // Ausführen bei jedem Pfadwechsel ist also folgenlos.
  useEffect(() => {
    if (!mounted) return;
    if (consent === 'granted' && isPublicMarketingPath(location.pathname)) {
      grantConsentAndLoad();
    } else if (consent === 'denied') {
      // Auch bei einem storage-Event aus einem anderen Tab muss der lokale
      // Google-Consent-State sofort widerrufen werden.
      denyConsent();
    }
  }, [mounted, consent, location.pathname, location.search, location.hash]);

  const show = mounted && consent === null && isGoogleAdsEnabled() && isPublicMarketingPath(location.pathname);

  // Solange der Banner sichtbar ist, unten Platz reservieren (Padding via
  // CSS), damit er keine Inhalte am Seitenende dauerhaft verdeckt.
  useEffect(() => {
    if (!show) {
      return undefined;
    }
    document.documentElement.classList.add(BANNER_VISIBLE_CLASS);
    return () => document.documentElement.classList.remove(BANNER_VISIBLE_CLASS);
  }, [show]);

  // MAJOR-5-Fix: 160px (CSS-Fallback) reicht bei umbrechendem Text und
  // gestapelten Buttons auf schmalen Viewports nicht aus — bannerRef misst
  // die tatsächliche Höhe und veröffentlicht sie als CSS-Custom-Property
  // (siehe CookieConsentBanner.css).
  const bannerRef = useBannerHeightCssVar(show, '--cookie-banner-height');

  // CRITICAL-1-Fix: Der sichtbare Bannerinhalt UND diese Ansage werden aus
  // demselben show-Wechsel gespeist, aber PoliteAnnouncer (siehe dort) ist
  // unabhängig von show dauerhaft gemountet — die Live-Region existiert also
  // bereits im Accessibility-Tree, BEVOR dieser Effekt ihren Text setzt.
  // Genau diese zeitliche Trennung (erst Region registrieren, dann Inhalt
  // ändern) ist es, was AT zur Ansage braucht; ein neu eingefügter, bereits
  // gefüllter Knoten würde stumm bleiben.
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    setAnnouncement(
      show
        ? 'Hinweis am Seitenende: Cookie-Einstellungen. Mit Ihrer Einwilligung nutzen wir ' +
            'Google Ads zur Messung unserer Werbekampagnen. Details in der ' +
            'Datenschutzerklärung. Wählen Sie „Ablehnen" oder „Akzeptieren".'
        : ''
    );
  }, [show]);

  // setConsent() muss hier NICHT mehr manuell aufgerufen werden: die
  // subscribeConsent()-Subscription oben erhält die Änderung über
  // notifySubscribers() synchron innerhalb von setMarketingConsent() und
  // aktualisiert den lokalen State bereits (MAJOR-4-Fix) — eine zusätzliche
  // manuelle Aktualisierung würde denselben Wert nur redundant setzen.
  const handleAccept = () => {
    setMarketingConsent('granted');
    grantConsentAndLoad();
    moveFocusAfterDismiss();
  };

  const handleReject = () => {
    setMarketingConsent('denied');
    denyConsent();
    moveFocusAfterDismiss();
  };

  return (
    <>
      {/* Muss unabhängig von "show" gemountet sein (siehe PoliteAnnouncer-
          JSDoc) — deshalb außerhalb der show-Bedingung unten. */}
      <PoliteAnnouncer id="cookie-consent-announcer" text={announcement} />
      {show && (
        <div
          ref={bannerRef}
          id="cookie-consent-banner"
          className="cookie-consent-banner shadow-lg"
          role="region"
          aria-label="Cookie-Einwilligung"
        >
          <div className="cookie-consent-text">
            <strong>Cookie-Einstellungen</strong>
            <span>
              Mit Ihrer Einwilligung nutzen wir Google Ads zur Messung
              unserer Werbekampagnen und für Remarketing.
              Details finden Sie in unserer{' '}
              <Link
                id="cookie-consent-privacy-link"
                to={buildPublicPath(location.pathname, '/datenschutz')}
              >
                Datenschutzerklärung
              </Link>
              .
            </span>
          </div>
          {/* MAJOR-6-Fix: beide Buttons nutzen dieselbe Variante (outline-primary)
              mit identischer Füllung/Kontrast — nicht nur dieselbe Größe. Die
              vorherige Kombination primary/outline-primary gewichtete
              "Akzeptieren" durch die volle Füllung optisch stärker als
              "Ablehnen", genau die Dark-Pattern-Konstellation, vor der die
              Datenschutzkonferenz in ihrer Orientierungshilfe zu
              Einwilligungsbannern warnt. outline-primary erfüllt WCAG 2.1 AA
              (Bootstrap-Standardkontrast von --bs-primary auf --bs-body-bg) für
              beide Buttons gleichermaßen. */}
          <div className="cookie-consent-actions">
            <Button
              id="cookie-consent-reject-button"
              variant="outline-primary"
              onClick={handleReject}
            >
              Ablehnen
            </Button>
            <Button
              id="cookie-consent-accept-button"
              variant="outline-primary"
              onClick={handleAccept}
            >
              Akzeptieren
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;
